import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Box, Paper, Typography, Grid, Select, MenuItem, FormControl, InputLabel, } from '@mui/material';
import { fK, fX, f$ } from '../utils/formatters';
// ── Colors / brand ─────────────────────────────────────────────────────────
const PINK = '#FF008E';
const INK = '#0A0A0A';
const T1_RED = '#B42318';
const T2_AMBER = '#CA8A04';
const EF_GREEN = '#027A48';
// ── Constants ──────────────────────────────────────────────────────────────
const BUDGET_INVERSION_TOP = 7;
const TOP_N_POOL_SIZE = 30;
const TOP_N_DISPLAY_ROWS = 15;
const UNDER_SCALED_TOP = 20;
const STRANDED_LOW = 100;
const POST_VERDICT_HIGH_SPEND = 10000;
// ── Adaptive defaults — matches deck behavior per client size ──────────────
function getDefaults(totalSpend) {
    if (totalSpend < 500000) {
        return { tierFloor: 500, poolFloor: 500, usMin: 200, usCap: 5000 };
    }
    return { tierFloor: 5000, poolFloor: 5000, usMin: 500, usCap: 30000 };
}
// EF cutoff display rounding (matches deck rounding behavior)
function getEfCutoff(accountROAS) {
    if (accountROAS < 5)
        return Math.ceil(accountROAS * 100) / 100;
    return Math.ceil(accountROAS * 10) / 10;
}
// Under-scaled ROAS default
function getDefaultUsROAS(accountROAS, efCutoff) {
    if (accountROAS < 3)
        return 2.0;
    return Math.round(2 * efCutoff * 10) / 10;
}
// Format $ in K (e.g. $5K, $30K)
const fKFloor = (v) => v >= 1000 ? `$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}K` : `$${v}`;
// ═══════════════════════════════════════════════════════════════════════════
// COMPUTE
// ═══════════════════════════════════════════════════════════════════════════
function computeProjectionsV2(report, cfg) {
    const rawAds = report?.ads || [];
    const counts = report?.counts || {};
    const ads = rawAds.map((a) => {
        const periodSpend = (a.atSpend || 0) + (a.afterSpend || 0);
        const periodRevenue = (a.atRevenue || 0) + (a.afterRevenue || 0);
        const periodClicks = (a.atClicks || 0) + (a.afterClicks || 0);
        const periodCtRev = (a.atCtRevenue || 0) + (a.afterCtRevenue || 0);
        const periodVtRev = (a.atVtRevenue || 0) + (a.afterVtRevenue || 0);
        const lifetimeROAS = periodSpend > 0 ? periodRevenue / periodSpend : 0;
        const ocpc = periodClicks > 0 ? periodSpend / periodClicks : 0;
        const ctROAS = periodSpend > 0 ? periodCtRev / periodSpend : 0;
        const vtROAS = periodSpend > 0 ? periodVtRev / periodSpend : 0;
        return {
            ...a,
            periodSpend,
            periodRevenue,
            periodClicks,
            lifetimeROAS,
            ocpc,
            ctROAS,
            vtROAS,
        };
    });
    // Account totals (all ads)
    const totalSpend = ads.reduce((s, a) => s + a.periodSpend, 0);
    const totalRevenue = ads.reduce((s, a) => s + a.periodRevenue, 0);
    const accountROAS = totalSpend > 0 ? totalRevenue / totalSpend : 0;
    const efCutoff = getEfCutoff(accountROAS);
    // Tier classification — periodSpend >= tierFloor
    const eligible = ads.filter((a) => a.periodSpend >= cfg.tierFloor);
    const T1 = eligible.filter((a) => a.lifetimeROAS < 1.0);
    const T2 = eligible.filter((a) => a.lifetimeROAS >= 1.0 && a.lifetimeROAS < efCutoff);
    const EF = eligible.filter((a) => a.lifetimeROAS >= efCutoff);
    const tierStats = (tier) => {
        const spend = tier.reduce((s, a) => s + a.periodSpend, 0);
        const revenue = tier.reduce((s, a) => s + a.periodRevenue, 0);
        return {
            count: tier.length,
            spend,
            revenue,
            roas: spend > 0 ? revenue / spend : 0,
        };
    };
    const T1Stats = tierStats(T1);
    const T2Stats = tierStats(T2);
    const EFStats = tierStats(EF);
    const excludedCount = ads.length - eligible.length;
    // Budget Inversion
    const topBySpend = [...ads]
        .sort((a, b) => b.periodSpend - a.periodSpend)
        .slice(0, BUDGET_INVERSION_TOP);
    const top2BySpend = topBySpend.slice(0, 2);
    const top2BySpendTotal = top2BySpend.reduce((s, a) => s + a.periodSpend, 0);
    const top2BySpendRev = top2BySpend.reduce((s, a) => s + a.periodRevenue, 0);
    const top2BySpendROAS = top2BySpendTotal > 0 ? top2BySpendRev / top2BySpendTotal : 0;
    // Top-30 pool — periodSpend >= poolFloor, sorted by lifetimeROAS desc
    const topPool = [...ads]
        .filter((a) => a.periodSpend >= cfg.poolFloor)
        .sort((a, b) => b.lifetimeROAS - a.lifetimeROAS)
        .slice(0, TOP_N_POOL_SIZE);
    const topPoolSpend = topPool.reduce((s, a) => s + a.periodSpend, 0);
    const topPoolRev = topPool.reduce((s, a) => s + a.periodRevenue, 0);
    const topPoolROAS = topPoolSpend > 0 ? topPoolRev / topPoolSpend : 0;
    const topPoolCtRev = topPool.reduce((s, a) => s + ((a.atCtRevenue || 0) + (a.afterCtRevenue || 0)), 0);
    const topPoolVtRev = topPool.reduce((s, a) => s + ((a.atVtRevenue || 0) + (a.afterVtRevenue || 0)), 0);
    const topPoolCtROAS = topPoolSpend > 0 ? topPoolCtRev / topPoolSpend : 0;
    const topPoolVtROAS = topPoolSpend > 0 ? topPoolVtRev / topPoolSpend : 0;
    const topPoolClicks = topPool.reduce((s, a) => s + a.periodClicks, 0);
    const topPoolOCPC = topPoolClicks > 0 ? topPoolSpend / topPoolClicks : 0;
    // Scenarios — all hold totalSpend constant
    const scenario = (reallocBudget, lostRev) => {
        const projRev = totalRevenue - lostRev + reallocBudget * topPoolROAS;
        const projROAS = totalSpend > 0 ? projRev / totalSpend : 0;
        const delta = accountROAS > 0 ? ((projROAS - accountROAS) / accountROAS) * 100 : 0;
        const gain = projRev - totalRevenue;
        return { reallocBudget, lostRev, projRev, projROAS, delta, gain };
    };
    const actual = scenario(0, 0);
    const conservative = scenario(T1Stats.spend, T1Stats.revenue);
    const moderate = scenario(T1Stats.spend + 0.5 * T2Stats.spend, T1Stats.revenue + 0.5 * T2Stats.revenue);
    const aggressive = scenario(T1Stats.spend + T2Stats.spend, T1Stats.revenue + T2Stats.revenue);
    // Under-Scaled
    const underScaled = ads
        .filter((a) => a.lifetimeROAS >= cfg.usROAS &&
        a.periodSpend >= cfg.usMin &&
        a.periodSpend < cfg.usCap)
        .sort((a, b) => b.lifetimeROAS - a.lifetimeROAS);
    const top20Under = underScaled.slice(0, UNDER_SCALED_TOP);
    const top20UnderSpend = top20Under.reduce((s, a) => s + a.periodSpend, 0);
    const top20UnderRev = top20Under.reduce((s, a) => s + a.periodRevenue, 0);
    const top20UnderROAS = top20UnderSpend > 0 ? top20UnderRev / top20UnderSpend : 0;
    const availableBudget = T1Stats.spend;
    const projAddRevUnder = availableBudget * top20UnderROAS;
    // ── Slide 9 findings ───────────────────────────────────────────────
    const finding1 = {
        top2Spend: top2BySpendTotal,
        top2ROAS: top2BySpendROAS,
        topPoolSpend,
        topPoolROAS,
    };
    const learningCount = counts.Learning || 0;
    const learningPct = ads.length > 0 ? Math.round((learningCount / ads.length) * 100) : 0;
    const thresholdValue = report?.threshold || 190;
    const stranded = ads.filter((a) => a.periodSpend >= STRANDED_LOW && a.periodSpend < thresholdValue).length;
    const finding2 = { learningCount, learningPct, stranded, threshold: thresholdValue };
    const efAds = EF.length ? [...EF] : [];
    efAds.sort((a, b) => b.lifetimeROAS - a.lifetimeROAS);
    const topWinners = efAds.slice(0, 2);
    const finding3 = {
        topWinners: topWinners.map((a) => ({
            name: a.ad_name,
            roas: a.lifetimeROAS,
            spend: a.periodSpend,
            atROAS: a.atROAS || 0,
        })),
    };
    const KEYWORDS = ['Adapt', 'Haley', 'Bre', 'Static', 'Video', 'Carousel', 'Evergreen', 'Promo'];
    const keywordStats = KEYWORDS.map((kw) => {
        const re = new RegExp(kw, 'i');
        let pass = 0, opt = 0, fail = 0;
        for (const a of ads) {
            if (!re.test(a.ad_name || ''))
                continue;
            if (a.result === 'Pass')
                pass++;
            else if (a.result === 'Optimize')
                opt++;
            else if (a.result === 'Fail')
                fail++;
        }
        return { keyword: kw, pass, opt, fail, total: pass + opt + fail };
    })
        .filter((k) => k.total >= 5 && k.fail >= k.pass)
        .sort((a, b) => b.fail - a.fail)
        .slice(0, 2);
    const finding4 = { patterns: keywordStats };
    const postVerdictHighSpenders = ads.filter((a) => (a.afterSpend || 0) > POST_VERDICT_HIGH_SPEND);
    const worstPostVerdict = [...postVerdictHighSpenders]
        .filter((a) => a.lifetimeROAS < accountROAS)
        .sort((a, b) => b.afterSpend - a.afterSpend)
        .slice(0, 1);
    const finding5 = {
        count: postVerdictHighSpenders.length,
        worst: worstPostVerdict[0]
            ? {
                name: worstPostVerdict[0].ad_name,
                afterSpend: worstPostVerdict[0].afterSpend,
                roas: worstPostVerdict[0].lifetimeROAS,
                netRev: worstPostVerdict[0].periodRevenue - worstPostVerdict[0].periodSpend,
            }
            : null,
    };
    return {
        totalSpend,
        totalRevenue,
        accountROAS,
        efCutoff,
        counts,
        adsCount: ads.length,
        evaluated: (counts.Pass || 0) + (counts.Optimize || 0) + (counts.Fail || 0),
        T1Stats,
        T2Stats,
        EFStats,
        excludedCount,
        topBySpend,
        top2BySpendTotal,
        top2BySpendROAS,
        topPool,
        topPoolSpend,
        topPoolRev,
        topPoolROAS,
        topPoolCtROAS,
        topPoolVtROAS,
        topPoolOCPC,
        actual,
        conservative,
        moderate,
        aggressive,
        underScaledCount: underScaled.length,
        top20Under,
        top20UnderSpend,
        top20UnderROAS,
        availableBudget,
        projAddRevUnder,
        finding1,
        finding2,
        finding3,
        finding4,
        finding5,
        failWastedSpend: report?.projections?.failWastedSpend || 0,
        optWastedSpend: report?.projections?.optWastedSpend || 0,
        failOptWastedSpend: report?.projections?.failOptWastedSpend || 0,
        avgPassROAS: report?.projections?.avgPassROAS || 0,
    };
}
// ═══════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════
const SectionHeader = ({ eyebrow, title, sub, }) => (_jsxs(Box, { sx: { mb: 2 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }, children: [_jsx(Box, { sx: { width: 20, height: 2, background: PINK } }), _jsx(Typography, { sx: {
                        fontSize: 10,
                        fontWeight: 700,
                        color: PINK,
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                    }, children: eyebrow })] }), _jsx(Typography, { sx: { fontSize: 22, fontWeight: 700, color: INK, lineHeight: 1.25 }, children: title }), sub && (_jsx(Typography, { sx: { fontSize: 12, color: '#667085', mt: 0.5, lineHeight: 1.55 }, children: sub }))] }));
const Stat = ({ label, value, sub, color, big }) => (_jsxs(Box, { sx: {
        p: big ? 2 : 1.5,
        border: '1px solid #EAECF0',
        borderRadius: '8px',
        background: '#fff',
        height: '100%',
    }, children: [_jsx(Typography, { sx: {
                fontSize: 10,
                fontWeight: 700,
                color: '#98A2B3',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                mb: 0.5,
            }, children: label }), _jsx(Typography, { sx: {
                fontSize: big ? 26 : 20,
                fontWeight: 700,
                color: color || INK,
                lineHeight: 1.15,
            }, children: value }), sub && (_jsx(Typography, { sx: { fontSize: 10.5, color: '#667085', mt: 0.5 }, children: sub }))] }));
// ═══════════════════════════════════════════════════════════════════════════
// CONFIG PANEL — adjustable floors at the top of the tab
// ═══════════════════════════════════════════════════════════════════════════
const FLOOR_OPTIONS = [200, 500, 1000, 2000, 5000, 10000];
const CAP_OPTIONS = [5000, 10000, 30000, 100000];
const ROAS_OPTIONS = [1.5, 2.0, 2.5, 3.0, 5.0, 10.0, 15.0, 16.0, 20.0];
const ConfigPanel = ({ cfg, onChange }) => {
    const upd = (k, v) => onChange({ ...cfg, [k]: v });
    return (_jsxs(Paper, { elevation: 0, sx: {
            p: 2,
            background: '#F9FAFB',
            border: '1px solid #EAECF0',
            borderRadius: '8px',
        }, children: [_jsx(Typography, { sx: {
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#475467',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    mb: 1.5,
                }, children: "\u2699 Computation Settings \u2014 Auto-selected based on account size; adjust if needed" }), _jsxs(Grid, { container: true, spacing: 1.5, children: [_jsx(Grid, { size: { xs: 12, sm: 6, md: 2.4 }, children: _jsxs(FormControl, { size: "small", fullWidth: true, children: [_jsx(InputLabel, { children: "Tier Floor" }), _jsx(Select, { label: "Tier Floor", value: cfg.tierFloor, onChange: (e) => upd('tierFloor', Number(e.target.value)), children: FLOOR_OPTIONS.map((v) => (_jsx(MenuItem, { value: v, children: fKFloor(v) }, v))) })] }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 2.4 }, children: _jsxs(FormControl, { size: "small", fullWidth: true, children: [_jsx(InputLabel, { children: "Top-30 Pool Floor" }), _jsx(Select, { label: "Top-30 Pool Floor", value: cfg.poolFloor, onChange: (e) => upd('poolFloor', Number(e.target.value)), children: FLOOR_OPTIONS.map((v) => (_jsx(MenuItem, { value: v, children: fKFloor(v) }, v))) })] }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 2.4 }, children: _jsxs(FormControl, { size: "small", fullWidth: true, children: [_jsx(InputLabel, { children: "Under-Scaled Min" }), _jsx(Select, { label: "Under-Scaled Min", value: cfg.usMin, onChange: (e) => upd('usMin', Number(e.target.value)), children: FLOOR_OPTIONS.map((v) => (_jsx(MenuItem, { value: v, children: fKFloor(v) }, v))) })] }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 2.4 }, children: _jsxs(FormControl, { size: "small", fullWidth: true, children: [_jsx(InputLabel, { children: "Under-Scaled Cap" }), _jsx(Select, { label: "Under-Scaled Cap", value: cfg.usCap, onChange: (e) => upd('usCap', Number(e.target.value)), children: CAP_OPTIONS.map((v) => (_jsx(MenuItem, { value: v, children: fKFloor(v) }, v))) })] }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 2.4 }, children: _jsxs(FormControl, { size: "small", fullWidth: true, children: [_jsx(InputLabel, { children: "Under-Scaled ROAS" }), _jsx(Select, { label: "Under-Scaled ROAS", value: cfg.usROAS, onChange: (e) => upd('usROAS', Number(e.target.value)), children: ROAS_OPTIONS.map((v) => (_jsxs(MenuItem, { value: v, children: [v, "x"] }, v))) })] }) })] })] }));
};
// ═══════════════════════════════════════════════════════════════════════════
// SECTIONS (unchanged layout, now driven by cfg)
// ═══════════════════════════════════════════════════════════════════════════
const AccountSnapshot = ({ v }) => (_jsxs(Box, { children: [_jsx(SectionHeader, { eyebrow: "Executive Summary", title: "The Account in Numbers", sub: "All figures period-filtered. Wasted-spend totals from saved projections; everything else recomputed from per-ad totals." }), _jsxs(Grid, { container: true, spacing: 1.5, sx: { mb: 1.5 }, children: [_jsx(Grid, { size: { xs: 6, md: 3 }, children: _jsx(Stat, { label: "Total Spend", value: fK(v.totalSpend), sub: "Report period", big: true }) }), _jsx(Grid, { size: { xs: 6, md: 3 }, children: _jsx(Stat, { label: "Total Revenue", value: fK(v.totalRevenue), sub: "Period attributed", big: true }) }), _jsx(Grid, { size: { xs: 6, md: 3 }, children: _jsx(Stat, { label: "Blended ROAS", value: fX(v.accountROAS), sub: "Revenue / spend", color: v.accountROAS >= 1.5 ? EF_GREEN : T1_RED, big: true }) }), _jsx(Grid, { size: { xs: 6, md: 3 }, children: _jsx(Stat, { label: "Ads Analysed", value: v.adsCount.toLocaleString('en-US'), sub: `Evaluated: ${v.evaluated.toLocaleString('en-US')}`, big: true }) })] }), _jsx(Grid, { container: true, spacing: 1.5, sx: { mb: 1.5 }, children: [
                { k: 'Pass', color: EF_GREEN, bg: '#ECFDF3' },
                { k: 'Optimize', color: T2_AMBER, bg: '#FFFAEB' },
                { k: 'Fail', color: T1_RED, bg: '#FEF3F2' },
                { k: 'Learning', color: '#175CD3', bg: '#EFF8FF' },
            ].map((c) => {
                const cnt = v.counts[c.k] || 0;
                const pct = v.evaluated > 0 && c.k !== 'Learning'
                    ? Math.round((cnt / v.evaluated) * 100)
                    : c.k === 'Learning' && v.adsCount > 0
                        ? Math.round((cnt / v.adsCount) * 100)
                        : 0;
                return (_jsx(Grid, { size: { xs: 6, md: 3 }, children: _jsxs(Box, { sx: { p: 1.5, background: c.bg, borderRadius: '8px' }, children: [_jsx(Typography, { sx: {
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: c.color,
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                }, children: c.k }), _jsx(Typography, { sx: { fontSize: 22, fontWeight: 700, color: c.color }, children: cnt.toLocaleString('en-US') }), _jsx(Typography, { sx: { fontSize: 10.5, color: c.color, opacity: 0.8 }, children: c.k === 'Learning' ? `${pct}% of total` : `${pct}% of evaluated` })] }) }, c.k));
            }) }), _jsxs(Box, { children: [_jsx(Typography, { sx: {
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#475467',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        mb: 1,
                    }, children: "Wasted Spend Overview" }), _jsxs(Grid, { container: true, spacing: 1.5, children: [_jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsx(Stat, { label: "Fail Ads Wasted", value: fK(v.failWastedSpend), color: T1_RED }) }), _jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsx(Stat, { label: "Optimize Ads Wasted", value: fK(v.optWastedSpend), color: T2_AMBER }) }), _jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsx(Stat, { label: "Fail + Optimize Combined", value: fK(v.failOptWastedSpend) }) }), _jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsx(Stat, { label: "Avg Pass ROAS", value: fX(v.avgPassROAS), color: EF_GREEN }) })] })] })] }));
const TierBreakdown = ({ v, cfg }) => {
    const renderCard = (label, sub, s, color, bg, desc) => (_jsxs(Paper, { elevation: 0, sx: {
            p: 2,
            background: bg,
            border: `2px solid ${color}`,
            borderRadius: '8px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
        }, children: [_jsx(Typography, { sx: { fontSize: 10.5, fontWeight: 700, color, letterSpacing: '0.08em' }, children: label }), _jsx(Typography, { sx: { fontSize: 11, color: '#667085', mb: 2 }, children: sub }), _jsx(Typography, { sx: {
                    fontSize: 9.5,
                    fontWeight: 700,
                    color: '#98A2B3',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                }, children: "Spend" }), _jsx(Typography, { sx: { fontSize: 28, fontWeight: 700, color, lineHeight: 1.05, mb: 1 }, children: fK(s.spend) }), _jsx(Typography, { sx: {
                    fontSize: 9.5,
                    fontWeight: 700,
                    color: '#98A2B3',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                }, children: "Revenue" }), _jsx(Typography, { sx: { fontSize: 20, fontWeight: 700, color: INK, lineHeight: 1.1, mb: 1 }, children: fK(s.revenue) }), _jsx(Typography, { sx: {
                    fontSize: 9.5,
                    fontWeight: 700,
                    color: '#98A2B3',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                }, children: "ROAS" }), _jsx(Typography, { sx: { fontSize: 24, fontWeight: 700, color, lineHeight: 1.1, mb: 2 }, children: fX(s.roas) }), _jsx(Typography, { sx: { fontSize: 11, color: '#667085', mt: 'auto', lineHeight: 1.5 }, children: desc })] }));
    return (_jsxs(Box, { children: [_jsx(SectionHeader, { eyebrow: "The Core Problem", title: "Budget Went to the Wrong Ads", sub: `Spend by efficiency tier — ranked purely by revenue per dollar, no result labels. ${v.excludedCount.toLocaleString('en-US')} ads excluded for periodSpend < ${fKFloor(cfg.tierFloor)}.` }), _jsxs(Grid, { container: true, spacing: 1.5, children: [_jsx(Grid, { size: { xs: 12, md: 4 }, children: renderCard('TIER 1 — LOSING MONEY', `ROAS < 1.0x · ${v.T1Stats.count} ads`, v.T1Stats, T1_RED, '#FEF3F2', 'Returned less than $1 per $1 spent') }), _jsx(Grid, { size: { xs: 12, md: 4 }, children: renderCard('TIER 2 — BELOW AVERAGE', `1.0x – ${fX(v.efCutoff)} · ${v.T2Stats.count} ads`, v.T2Stats, T2_AMBER, '#FFFAEB', 'Generating revenue but well below account average') }), _jsx(Grid, { size: { xs: 12, md: 4 }, children: renderCard('HIGH EFFICIENCY', `≥ ${fX(v.efCutoff)} · ${v.EFStats.count} ads`, v.EFStats, EF_GREEN, '#ECFDF3', 'Proven reallocation destinations') })] })] }));
};
const BudgetInversion = ({ v }) => (_jsxs(Box, { children: [_jsx(SectionHeader, { eyebrow: "Budget Inversion", title: "Biggest Spenders Were Weakest Performers", sub: `Top ${BUDGET_INVERSION_TOP} ads by total spend vs what they actually returned` }), _jsxs(Paper, { elevation: 0, sx: { border: '1px solid #EAECF0', borderRadius: '8px', overflow: 'hidden' }, children: [_jsxs(Box, { sx: {
                        display: 'grid',
                        gridTemplateColumns: '40px minmax(220px, 1fr) 110px 90px',
                        gap: 1,
                        px: 1.75,
                        py: 1.25,
                        background: '#F9FAFB',
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: '#475467',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                    }, children: [_jsx("span", { children: "#" }), _jsx("span", { children: "Ad Name" }), _jsx("span", { style: { textAlign: 'right' }, children: "Spend" }), _jsx("span", { style: { textAlign: 'right' }, children: "ROAS" })] }), v.topBySpend.map((a, i) => (_jsxs(Box, { sx: {
                        display: 'grid',
                        gridTemplateColumns: '40px minmax(220px, 1fr) 110px 90px',
                        gap: 1,
                        px: 1.75,
                        py: 1.1,
                        borderTop: '1px solid #F2F4F7',
                        fontSize: 12,
                        alignItems: 'center',
                    }, children: [_jsx("span", { style: { color: '#98A2B3', fontFamily: 'monospace' }, children: String(i + 1).padStart(2, '0') }), _jsx("span", { title: a.ad_name, style: {
                                color: INK,
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }, children: a.ad_name }), _jsx("span", { style: { textAlign: 'right', color: INK, fontWeight: 600 }, children: fK(a.periodSpend) }), _jsx("span", { style: {
                                textAlign: 'right',
                                fontWeight: 700,
                                color: a.lifetimeROAS < v.accountROAS ? T1_RED : EF_GREEN,
                            }, children: fX(a.lifetimeROAS) })] }, a.ad_id || i)))] }), _jsx(Paper, { elevation: 0, sx: {
                mt: 1.5,
                p: 1.5,
                background: '#FEF3F2',
                borderRadius: '8px',
                borderLeft: `3px solid ${T1_RED}`,
            }, children: _jsxs(Typography, { sx: { fontSize: 11.5, color: '#475467', lineHeight: 1.6 }, children: ["The top 2 ads alone received", ' ', _jsx("strong", { style: { color: INK }, children: fK(v.top2BySpendTotal) }), " at average", ' ', _jsxs("strong", { style: {
                            color: v.top2BySpendROAS < v.accountROAS ? T1_RED : EF_GREEN,
                        }, children: [fX(v.top2BySpendROAS), " ROAS"] }), ' ', "\u2014 while the best-performing ads averaged", ' ', _jsx("strong", { style: { color: EF_GREEN }, children: fX(v.topPoolROAS) }), " but received under", ' ', _jsx("strong", { style: { color: INK }, children: fK(v.topPoolSpend) }), " combined."] }) })] }));
const Top30Targets = ({ v, cfg }) => {
    const rows = v.topPool.slice(0, TOP_N_DISPLAY_ROWS);
    return (_jsxs(Box, { children: [_jsx(SectionHeader, { eyebrow: "Reallocation Targets", title: `Top ${Math.min(TOP_N_DISPLAY_ROWS, v.topPool.length)} Best-Performing Ads — The ${fX(v.topPoolROAS)} Targets`, sub: v.topPool.length > 0
                    ? `Ranked by period ROAS (min ${fKFloor(cfg.poolFloor)} spend) · Combined: ${fK(v.topPoolRev)} revenue on ${fK(v.topPoolSpend)} spend = ${fX(v.topPoolROAS)} weighted avg`
                    : `No ads qualify (need periodSpend ≥ ${fKFloor(cfg.poolFloor)})` }), rows.length > 0 && (_jsxs(Paper, { elevation: 0, sx: { border: '1px solid #EAECF0', borderRadius: '8px', overflow: 'hidden' }, children: [_jsxs(Box, { sx: {
                            display: 'grid',
                            gridTemplateColumns: '40px minmax(200px, 1fr) 80px 90px 70px 70px 70px 70px',
                            gap: 1,
                            px: 1.75,
                            py: 1.25,
                            background: '#F9FAFB',
                            fontSize: 10.5,
                            fontWeight: 700,
                            color: '#475467',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                        }, children: [_jsx("span", { children: "#" }), _jsx("span", { children: "Ad Name" }), _jsx("span", { style: { textAlign: 'right' }, children: "Spend" }), _jsx("span", { style: { textAlign: 'right' }, children: "Revenue" }), _jsx("span", { style: { textAlign: 'right' }, children: "ROAS" }), _jsx("span", { style: { textAlign: 'right' }, children: "CT ROAS" }), _jsx("span", { style: { textAlign: 'right' }, children: "VT ROAS" }), _jsx("span", { style: { textAlign: 'right' }, children: "OCPC" })] }), rows.map((ad, i) => (_jsxs(Box, { sx: {
                            display: 'grid',
                            gridTemplateColumns: '40px minmax(200px, 1fr) 80px 90px 70px 70px 70px 70px',
                            gap: 1,
                            px: 1.75,
                            py: 1.1,
                            borderTop: '1px solid #F2F4F7',
                            fontSize: 11.5,
                            alignItems: 'center',
                        }, children: [_jsx("span", { style: { color: '#98A2B3', fontFamily: 'monospace' }, children: String(i + 1).padStart(2, '0') }), _jsx("span", { title: ad.ad_name, style: {
                                    color: INK,
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }, children: ad.ad_name }), _jsx("span", { style: { textAlign: 'right', color: '#475467' }, children: fK(ad.periodSpend) }), _jsx("span", { style: { textAlign: 'right', color: '#475467', fontWeight: 500 }, children: fK(ad.periodRevenue) }), _jsx("span", { style: { textAlign: 'right', color: PINK, fontWeight: 700 }, children: fX(ad.lifetimeROAS) }), _jsx("span", { style: { textAlign: 'right', color: '#475467' }, children: fX(ad.ctROAS) }), _jsx("span", { style: { textAlign: 'right', color: '#475467' }, children: fX(ad.vtROAS) }), _jsx("span", { style: { textAlign: 'right', color: '#475467' }, children: ad.ocpc > 0 ? f$(ad.ocpc) : '—' })] }, ad.ad_id || i))), _jsxs(Box, { sx: {
                            display: 'grid',
                            gridTemplateColumns: '40px minmax(200px, 1fr) 80px 90px 70px 70px 70px 70px',
                            gap: 1,
                            px: 1.75,
                            py: 1.25,
                            background: INK,
                            color: '#fff',
                            fontSize: 11.5,
                            fontWeight: 700,
                            borderTop: '1px solid #1A1A1A',
                        }, children: [_jsx("span", {}), _jsxs("span", { style: {
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                    fontSize: 10.5,
                                    color: '#A9A9A9',
                                }, children: ["Combined Top ", v.topPool.length, " \u00B7 Weighted Avg"] }), _jsx("span", { style: { textAlign: 'right' }, children: fK(v.topPoolSpend) }), _jsx("span", { style: { textAlign: 'right' }, children: fK(v.topPoolRev) }), _jsx("span", { style: { textAlign: 'right', color: PINK }, children: fX(v.topPoolROAS) }), _jsx("span", { style: { textAlign: 'right' }, children: fX(v.topPoolCtROAS) }), _jsx("span", { style: { textAlign: 'right' }, children: fX(v.topPoolVtROAS) }), _jsx("span", { style: { textAlign: 'right' }, children: v.topPoolOCPC > 0 ? f$(v.topPoolOCPC) : '—' })] })] }))] }));
};
const RevenueBridge = ({ v }) => (_jsxs(Box, { children: [_jsx(SectionHeader, { eyebrow: "How the Projections Work", title: "The Revenue Bridge \u2014 Conservative Scenario", sub: "What happens if we pause losing ads and redirect that budget to top performers" }), _jsxs(Grid, { container: true, spacing: 1.5, children: [_jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsxs(Paper, { elevation: 0, sx: { p: 2, border: '1px solid #EAECF0', borderRadius: '8px', height: '100%' }, children: [_jsx(Typography, { sx: {
                                    fontSize: 10.5,
                                    fontWeight: 700,
                                    color: '#475467',
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    mb: 0.5,
                                }, children: "Current Revenue" }), _jsx(Typography, { sx: { fontSize: 28, fontWeight: 700, color: INK }, children: fK(v.totalRevenue) }), _jsx(Typography, { sx: { fontSize: 11, color: '#667085', mt: 1 }, children: "Actual period total" })] }) }), _jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsxs(Paper, { elevation: 0, sx: {
                            p: 2,
                            border: `2px solid ${T1_RED}`,
                            borderRadius: '8px',
                            background: '#FEF3F2',
                            height: '100%',
                        }, children: [_jsx(Typography, { sx: {
                                    fontSize: 10.5,
                                    fontWeight: 700,
                                    color: T1_RED,
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    mb: 0.5,
                                }, children: "Revenue Lost" }), _jsxs(Typography, { sx: { fontSize: 28, fontWeight: 700, color: T1_RED }, children: ["\u2212", fK(v.T1Stats.revenue)] }), _jsxs(Typography, { sx: { fontSize: 11, color: '#667085', mt: 1 }, children: [v.T1Stats.count, " losing ads paused (ROAS < 1x)"] })] }) }), _jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsxs(Paper, { elevation: 0, sx: {
                            p: 2,
                            border: `2px solid ${EF_GREEN}`,
                            borderRadius: '8px',
                            background: '#ECFDF3',
                            height: '100%',
                        }, children: [_jsx(Typography, { sx: {
                                    fontSize: 10.5,
                                    fontWeight: 700,
                                    color: EF_GREEN,
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    mb: 0.5,
                                }, children: "Revenue Gained" }), _jsxs(Typography, { sx: { fontSize: 28, fontWeight: 700, color: EF_GREEN }, children: ["+", fK(v.T1Stats.spend * v.topPoolROAS)] }), _jsxs(Typography, { sx: { fontSize: 11, color: '#667085', mt: 1 }, children: [fK(v.T1Stats.spend), " \u00D7 ", fX(v.topPoolROAS), " top-30 ROAS"] })] }) }), _jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsxs(Paper, { elevation: 0, sx: { p: 2, border: `2px solid ${PINK}`, borderRadius: '8px', height: '100%' }, children: [_jsx(Typography, { sx: {
                                    fontSize: 10.5,
                                    fontWeight: 700,
                                    color: PINK,
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    mb: 0.5,
                                }, children: "Projected" }), _jsx(Typography, { sx: { fontSize: 28, fontWeight: 700, color: PINK }, children: fK(v.conservative.projRev) }), _jsxs(Typography, { sx: { fontSize: 11, color: '#667085', mt: 1 }, children: ["ROAS: ", fX(v.conservative.projROAS)] })] }) })] }), _jsx(Paper, { elevation: 0, sx: { mt: 1.5, p: 1.5, background: '#F9FAFB', borderRadius: '6px' }, children: _jsxs(Typography, { sx: { fontSize: 11.5, color: '#475467', textAlign: 'center', lineHeight: 1.6 }, children: [_jsx("strong", { style: { color: INK }, children: fK(v.totalRevenue) }), " (current)", ' ', _jsxs("strong", { style: { color: T1_RED }, children: ["\u2212 ", fK(v.T1Stats.revenue)] }), " (lost)", ' ', _jsxs("strong", { style: { color: EF_GREEN }, children: ["+ ", fK(v.T1Stats.spend * v.topPoolROAS)] }), ' ', "(", fK(v.T1Stats.spend), " \u00D7 ", fX(v.topPoolROAS), ") =", ' ', _jsx("strong", { style: { color: PINK }, children: fK(v.conservative.projRev) }), " projected"] }) }), _jsxs(Typography, { sx: { fontSize: 11, color: '#667085', mt: 1.5, lineHeight: 1.55 }, children: [_jsxs("strong", { style: { color: INK }, children: ["What is ", fX(v.topPoolROAS), "?"] }), " Weighted average ROAS of the top ", v.topPool.length, " ads \u2014 ", fK(v.topPoolRev), " revenue on", ' ', fK(v.topPoolSpend), " combined spend during the same period."] })] }));
const ScenarioCards = ({ v }) => {
    const card = (tag, name, desc, s, opts = {}) => {
        const { isBaseline, dark } = opts;
        const text = dark ? '#fff' : INK;
        const sub = dark ? '#A9A9A9' : '#667085';
        const accent = dark ? PINK : INK;
        return (_jsxs(Paper, { elevation: 0, sx: {
                p: 2,
                background: dark ? INK : '#fff',
                border: dark ? 'none' : isBaseline ? `2px solid ${INK}` : '1px solid #E6E6E6',
                borderRadius: '8px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }, children: [_jsx(Typography, { sx: {
                        fontSize: 10,
                        fontWeight: 700,
                        color: dark ? PINK : '#98A2B3',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        mb: 0.5,
                    }, children: tag }), _jsx(Typography, { sx: { fontSize: 18, fontWeight: 700, color: text, mb: 0.5 }, children: name }), _jsx(Typography, { sx: { fontSize: 11, color: sub, lineHeight: 1.5, mb: 1.5, minHeight: 36 }, children: desc }), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }, children: [_jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between', fontSize: 11.5 }, children: [_jsx("span", { style: { color: sub }, children: "Total Spend" }), _jsx("span", { style: { color: text, fontWeight: 600 }, children: fK(v.totalSpend) })] }), _jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between', fontSize: 11.5 }, children: [_jsx("span", { style: { color: sub }, children: "Projected Revenue" }), _jsx("span", { style: { color: text, fontWeight: 600 }, children: fK(s.projRev) })] }), _jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between', fontSize: 11.5 }, children: [_jsx("span", { style: { color: sub }, children: "Projected ROAS" }), _jsx("span", { style: { color: dark ? PINK : accent, fontWeight: 700 }, children: fX(s.projROAS) })] })] }), _jsxs(Box, { sx: {
                        mt: 'auto',
                        p: 1.25,
                        background: dark ? '#1A1A1A' : '#F5F5F5',
                        borderRadius: '8px',
                    }, children: [_jsx(Typography, { sx: {
                                fontSize: 9.5,
                                fontWeight: 700,
                                color: dark ? '#A9A9A9' : '#667085',
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                mb: 0.25,
                            }, children: "Revenue Gain" }), isBaseline ? (_jsxs(_Fragment, { children: [_jsx(Typography, { sx: { fontSize: 20, fontWeight: 700, color: text }, children: "\u2014" }), _jsx(Typography, { sx: { fontSize: 9.5, color: sub, mt: 0.25 }, children: "BASELINE" })] })) : (_jsxs(_Fragment, { children: [_jsxs(Typography, { sx: {
                                        fontSize: 20,
                                        fontWeight: 700,
                                        color: dark ? PINK : EF_GREEN,
                                    }, children: [s.gain >= 0 ? '+' : '', fK(s.gain)] }), _jsxs(Typography, { sx: { fontSize: 9.5, color: sub, mt: 0.25 }, children: [s.delta >= 0 ? '+' : '', s.delta.toFixed(0), "% ROAS"] })] }))] })] }));
    };
    return (_jsxs(Box, { children: [_jsx(SectionHeader, { eyebrow: "Projection Scenarios", title: `Three Paths — Same ${fK(v.totalSpend)} Total Spend`, sub: "Budget reallocated, not removed. Conservative to Aggressive." }), _jsxs(Grid, { container: true, spacing: 1.5, children: [_jsx(Grid, { size: { xs: 12, sm: 6, md: 3 }, children: card('BASELINE', 'Actual', 'Real performance as spent', v.actual, {
                            isBaseline: true,
                        }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 3 }, children: card('LOW RISK', 'Conservative', `Pause ${v.T1Stats.count} losing ads (ROAS < 1.0x)`, v.conservative) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 3 }, children: card('BALANCED', 'Moderate', 'Losing + 50% of below-average ads', v.moderate) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 3 }, children: card('UPSIDE', 'Aggressive', 'All sub-benchmark ads reallocated', v.aggressive, { dark: true }) })] }), _jsx(Paper, { elevation: 0, sx: {
                    mt: 1.5,
                    p: 1.25,
                    background: '#F5F5F5',
                    borderRadius: '6px',
                    borderLeft: `3px solid ${PINK}`,
                }, children: _jsxs(Typography, { sx: { fontSize: 11, color: '#475467', lineHeight: 1.6 }, children: ["Total spend unchanged in all scenarios. Budget is reallocated \u2014 not removed.", _jsxs("em", { children: [' ', "Projection = (current rev \u2212 lost rev) + (reallocated budget \u00D7", ' ', fX(v.topPoolROAS), " top-30 ROAS)."] })] }) })] }));
};
const HiddenOpportunity = ({ v, cfg }) => (_jsxs(Box, { children: [_jsx(SectionHeader, { eyebrow: "Hidden Opportunity", title: "High-Potential Ads That Were Never Funded", sub: `${v.underScaledCount} ads hit ≥ ${cfg.usROAS}x ROAS with periodSpend ∈ [${fKFloor(cfg.usMin)}, ${fKFloor(cfg.usCap)}). Top ${Math.min(UNDER_SCALED_TOP, v.top20Under.length)} averaged ${fX(v.top20UnderROAS)}.` }), _jsxs(Grid, { container: true, spacing: 1.5, children: [_jsxs(Grid, { size: { xs: 12, md: 5 }, children: [_jsxs(Grid, { container: true, spacing: 1.25, children: [_jsx(Grid, { size: 6, children: _jsx(Stat, { label: `Top ${v.top20Under.length} Avg ROAS`, value: fX(v.top20UnderROAS), sub: "Proven efficiency", color: PINK }) }), _jsx(Grid, { size: 6, children: _jsx(Stat, { label: "Their Total Spend", value: fK(v.top20UnderSpend), sub: `Across ${v.top20Under.length} ads` }) }), _jsx(Grid, { size: 6, children: _jsx(Stat, { label: "Available Budget", value: fK(v.availableBudget), sub: "From T1 losing pool" }) }), _jsx(Grid, { size: 6, children: _jsx(Stat, { label: "Proj. Additional Rev.", value: fK(v.projAddRevUnder), sub: `At ${fX(v.top20UnderROAS)} ROAS`, color: PINK }) })] }), _jsxs(Paper, { elevation: 0, sx: { mt: 1.5, p: 2, background: INK, borderRadius: '8px', color: '#fff' }, children: [_jsx(Typography, { sx: {
                                        fontSize: 10.5,
                                        fontWeight: 700,
                                        color: PINK,
                                        letterSpacing: '0.08em',
                                        mb: 1,
                                    }, children: "\u26A1 KEY INSIGHT" }), _jsxs(Typography, { sx: { fontSize: 12, color: '#fff', lineHeight: 1.65, mb: 1 }, children: ["These ", v.top20Under.length, " ads averaged", ' ', _jsx("strong", { style: { color: PINK }, children: fX(v.top20UnderROAS) }), " ROAS \u2014", ' ', (v.top20UnderROAS / Math.max(v.accountROAS, 0.01)).toFixed(1), "\u00D7 the account average of ", fX(v.accountROAS), "."] }), v.top20Under.length > 0 && (_jsxs(Typography, { sx: { fontSize: 12, color: '#fff', lineHeight: 1.65, mb: 1 }, children: ["None received more than", ' ', _jsx("strong", { style: { color: PINK }, children: fK(Math.max(...v.top20Under.map((a) => a.periodSpend))) }), ". Their spend was minimal \u2014 their performance was exceptional."] })), _jsxs(Typography, { sx: { fontSize: 12, color: '#fff', lineHeight: 1.65 }, children: ["Redirecting", ' ', _jsx("strong", { style: { color: PINK }, children: fK(v.availableBudget) }), " from losing ads to these performers projects an additional", ' ', _jsxs("strong", { style: { color: PINK }, children: ["+", fK(v.projAddRevUnder)] }), " in revenue."] })] })] }), _jsxs(Grid, { size: { xs: 12, md: 7 }, children: [_jsx(Typography, { sx: {
                                fontSize: 11,
                                fontWeight: 700,
                                color: PINK,
                                letterSpacing: '0.08em',
                                mb: 1,
                            }, children: "TOP UNDER-SCALED ADS" }), _jsxs(Paper, { elevation: 0, sx: { border: '1px solid #EAECF0', borderRadius: '8px', overflow: 'hidden' }, children: [_jsxs(Box, { sx: {
                                        display: 'grid',
                                        gridTemplateColumns: 'minmax(180px, 1fr) 80px 80px',
                                        gap: 1,
                                        px: 1.75,
                                        py: 1.1,
                                        background: '#F9FAFB',
                                        fontSize: 10,
                                        fontWeight: 700,
                                        color: '#475467',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.04em',
                                    }, children: [_jsx("span", { children: "Ad Name" }), _jsx("span", { style: { textAlign: 'right' }, children: "Spend" }), _jsx("span", { style: { textAlign: 'right' }, children: "ROAS" })] }), v.top20Under.slice(0, 10).map((ad, i) => (_jsxs(Box, { sx: {
                                        display: 'grid',
                                        gridTemplateColumns: 'minmax(180px, 1fr) 80px 80px',
                                        gap: 1,
                                        px: 1.75,
                                        py: 1,
                                        borderTop: '1px solid #F2F4F7',
                                        fontSize: 11.5,
                                        alignItems: 'center',
                                    }, children: [_jsx("span", { title: ad.ad_name, style: {
                                                color: INK,
                                                fontWeight: 500,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }, children: ad.ad_name }), _jsx("span", { style: { textAlign: 'right', color: '#475467' }, children: fK(ad.periodSpend) }), _jsx("span", { style: { textAlign: 'right', color: PINK, fontWeight: 700 }, children: fX(ad.lifetimeROAS) })] }, ad.ad_id || i)))] })] })] })] }));
const AccountStory = ({ v }) => {
    const findings = [];
    if (v.finding1.top2Spend > 0) {
        findings.push({
            num: '01',
            title: 'Budget concentration in the wrong ads',
            body: (_jsxs(_Fragment, { children: ["The top 2 ads by spend received", ' ', _jsx("strong", { style: { color: INK }, children: fK(v.finding1.top2Spend) }), " between them at", ' ', _jsx("strong", { style: {
                            color: v.finding1.top2ROAS < v.accountROAS ? T1_RED : EF_GREEN,
                        }, children: fX(v.finding1.top2ROAS) }), ' ', "average ROAS. The top ", v.topPool.length, " performers received", ' ', _jsx("strong", { style: { color: INK }, children: fK(v.finding1.topPoolSpend) }), " combined at", ' ', _jsx("strong", { style: { color: EF_GREEN }, children: fX(v.finding1.topPoolROAS) }), "."] })),
            color: PINK,
        });
    }
    if (v.finding2.learningCount > 0) {
        findings.push({
            num: '02',
            title: `${v.finding2.learningCount.toLocaleString('en-US')} ads never properly evaluated`,
            body: (_jsxs(_Fragment, { children: [v.finding2.learningPct, "% of all ads tested never hit the $", v.finding2.threshold, ' ', "evaluation threshold.", v.finding2.stranded > 0 && (_jsxs(_Fragment, { children: [' ', _jsx("strong", { style: { color: INK }, children: v.finding2.stranded.toLocaleString('en-US') }), ' ', "spent $100\u2013$", v.finding2.threshold, " and were cut before any verdict could be made."] }))] })),
            color: T2_AMBER,
        });
    }
    if (v.finding3.topWinners.length > 0) {
        const w = v.finding3.topWinners;
        findings.push({
            num: '03',
            title: "Winners weren't scaled aggressively enough",
            body: (_jsxs(_Fragment, { children: [_jsx("strong", { style: { color: INK }, children: w[0].name }), " returned", ' ', _jsx("strong", { style: { color: EF_GREEN }, children: fX(w[0].roas) }), " ROAS on", ' ', fK(w[0].spend), ".", w[1] && (_jsxs(_Fragment, { children: [' ', _jsx("strong", { style: { color: INK }, children: w[1].name }), " showed", ' ', _jsx("strong", { style: { color: EF_GREEN }, children: fX(w[1].roas) }), " on", ' ', fK(w[1].spend), "."] })), ' ', "Neither received meaningful scale."] })),
            color: EF_GREEN,
        });
    }
    if (v.finding4.patterns.length > 0) {
        findings.push({
            num: '04',
            title: 'Creative patterns show repeated failures',
            body: (_jsxs(_Fragment, { children: [v.finding4.patterns.map((p, i) => (_jsxs("span", { children: [i > 0 && ' ', "'", _jsx("strong", { style: { color: INK }, children: p.keyword }), "' variants: ", p.fail, ' ', "Fail, ", p.opt, " Optimize, ", p.pass, " Pass."] }, i))), ' ', "Same formats kept running despite repeated signals."] })),
            color: '#175CD3',
        });
    }
    if (v.finding5.count > 0 && v.finding5.worst) {
        findings.push({
            num: '05',
            title: 'No decision framework for pausing',
            body: (_jsxs(_Fragment, { children: [_jsx("strong", { style: { color: INK }, children: v.finding5.count.toLocaleString('en-US') }), ' ', "individual ads each spent more than ", fK(POST_VERDICT_HIGH_SPEND), " after their verdict.", ' ', _jsx("strong", { style: { color: INK }, children: v.finding5.worst.name }), " spent", ' ', fK(v.finding5.worst.afterSpend), " at ", fX(v.finding5.worst.roas), " ROAS \u2014", ' ', fK(v.finding5.worst.netRev), " net."] })),
            color: T1_RED,
        });
    }
    return (_jsxs(Box, { children: [_jsx(SectionHeader, { eyebrow: "Account Story", title: "What the Data Is Really Telling Us", sub: `${findings.length} patterns that explain the ${fX(v.accountROAS)} blended ROAS` }), _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: findings.map((f, i) => (_jsxs(Paper, { elevation: 0, sx: {
                        display: 'grid',
                        gridTemplateColumns: '50px 1fr',
                        gap: 1.5,
                        p: 1.75,
                        border: '1px solid #EAECF0',
                        borderLeft: `4px solid ${f.color}`,
                        borderRadius: '8px',
                        background: '#fff',
                    }, children: [_jsx(Typography, { sx: {
                                fontSize: 26,
                                fontWeight: 700,
                                color: f.color,
                                lineHeight: 1,
                                fontFamily: 'monospace',
                            }, children: f.num }), _jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: 14, fontWeight: 700, color: INK, mb: 0.5 }, children: f.title }), _jsx(Typography, { sx: { fontSize: 12, color: '#475467', lineHeight: 1.65 }, children: f.body })] })] }, i))) })] }));
};
const TheOpportunity = ({ v }) => (_jsxs(Box, { children: [_jsx(SectionHeader, { eyebrow: "The Opportunity", title: "Your account is generating revenue. The system to maximise it is missing." }), _jsx(Grid, { container: true, spacing: 1.5, children: [
                { name: 'Conservative Gain', gain: v.conservative.gain, sub: 'Pause losing ads only' },
                { name: 'Moderate Gain', gain: v.moderate.gain, sub: 'Fail + 50% below-avg reallocated' },
                { name: 'Aggressive Gain', gain: v.aggressive.gain, sub: 'All sub-benchmark reallocated' },
            ].map((s, i) => (_jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsxs(Paper, { elevation: 0, sx: {
                        p: 2.5,
                        background: i === 2 ? INK : '#fff',
                        border: i === 2 ? 'none' : '1px solid #EAECF0',
                        borderRadius: '8px',
                        textAlign: 'center',
                    }, children: [_jsx(Typography, { sx: {
                                fontSize: 11,
                                fontWeight: 700,
                                color: i === 2 ? PINK : '#475467',
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                mb: 1,
                            }, children: s.name }), _jsxs(Typography, { sx: {
                                fontSize: 32,
                                fontWeight: 700,
                                color: i === 2 ? PINK : EF_GREEN,
                                lineHeight: 1.1,
                                mb: 0.5,
                            }, children: ["+", fK(s.gain)] }), _jsx(Typography, { sx: { fontSize: 11, color: i === 2 ? '#A9A9A9' : '#667085' }, children: s.sub })] }) }, i))) }), _jsxs(Typography, { sx: { fontSize: 12, color: '#667085', textAlign: 'center', mt: 2, lineHeight: 1.6 }, children: ["Same total spend of ", _jsx("strong", { style: { color: INK }, children: fK(v.totalSpend) }), ". No additional budget required. The difference is allocation."] })] }));
// ═══════════════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════════════
const ProjectionsV2Tab = ({ report }) => {
    // Compute totalSpend + accountROAS once for default initialization
    const initialDefaults = useMemo(() => {
        const ads = report?.ads || [];
        const totalSpend = ads.reduce((s, a) => s + (a.atSpend || 0) + (a.afterSpend || 0), 0);
        const totalRevenue = ads.reduce((s, a) => s + (a.atRevenue || 0) + (a.afterRevenue || 0), 0);
        const accountROAS = totalSpend > 0 ? totalRevenue / totalSpend : 0;
        const ef = getEfCutoff(accountROAS);
        const dft = getDefaults(totalSpend);
        return {
            ...dft,
            usROAS: getDefaultUsROAS(accountROAS, ef),
        };
    }, [report]);
    const [cfg, setCfg] = useState(initialDefaults);
    const v = useMemo(() => computeProjectionsV2(report, cfg), [report, cfg]);
    return (_jsxs(Box, { sx: { p: 3, display: 'flex', flexDirection: 'column', gap: 4 }, children: [_jsx(ConfigPanel, { cfg: cfg, onChange: setCfg }), _jsx(AccountSnapshot, { v: v }), _jsx(TierBreakdown, { v: v, cfg: cfg }), _jsx(BudgetInversion, { v: v }), _jsx(Top30Targets, { v: v, cfg: cfg }), _jsx(RevenueBridge, { v: v }), _jsx(ScenarioCards, { v: v }), _jsx(HiddenOpportunity, { v: v, cfg: cfg }), _jsx(AccountStory, { v: v }), _jsx(TheOpportunity, { v: v })] }));
};
export default ProjectionsV2Tab;
export { computeProjectionsV2, getDefaults, getEfCutoff, getDefaultUsROAS };
